import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Building2, ChevronRight, Loader2 } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { cn } from "@/lib/utils";
import { toast } from "sonner";
import ultureLogo from "@/assets/ulture-logo-transparent.png";
import { ImgOffline } from "@/components/offline/ImgOffline";

interface LinkedCompany {
  company_id: string;
  company_name: string;
  company_logo: string | null;
  is_usable?: boolean;
}

interface PeritoCompanySelectorProps {
  companies: LinkedCompany[];
  currentCompanyId: string | null;
  onSelect: (companyId: string) => Promise<void>;
  /** Aviso encima de la lista (p. ej. «la empresa X no tiene la suscripción activa»). */
  aviso?: string | null;
}

/**
 * Selector de empresa al entrar. Tanda 5 (M40 / T15-06): una empresa sin
 * suscripción sale marcada y no se puede elegir, y el router lo enseña también
 * cuando la empresa en la que estaba el perito caduca, para que pase a otra en
 * vez de quedarse encerrado en la pantalla de pago de un cliente.
 */
export function PeritoCompanySelector({ companies, currentCompanyId, onSelect, aviso }: PeritoCompanySelectorProps) {
  const { t } = useTranslation();
  const [switching, setSwitching] = useState<string | null>(null);

  const handleSelect = async (companyId: string) => {
    if (switching) return;
    setSwitching(companyId);
    try {
      await onSelect(companyId);
    } catch (err) {
      console.error("Switch company error:", err);
      toast.error(t("perito.multiCompany.switchError"));
    } finally {
      setSwitching(null);
    }
  };

  return (
    <div className="min-h-screen flex flex-col items-center justify-center px-4" style={{ background: "var(--bg-page)" }}>
      <div className="w-full max-w-md space-y-8">
        {/* Header */}
        <div className="text-center space-y-3">
          <img src={ultureLogo} alt="Ulture" className="h-12 w-12 mx-auto object-contain" />
          <h1 className="text-2xl font-bold text-foreground tracking-tight">
            {t("perito.multiCompany.selectTitle")}
          </h1>
          <p className="text-muted-foreground text-sm">
            {t("perito.multiCompany.selectSubtitle")}
          </p>
        </div>

        {aviso && (
          <p role="status" className="rounded-xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-900" data-testid="perito-selector-aviso">
            {aviso}
          </p>
        )}

        {/* Company cards */}
        <div className="space-y-3">
          {companies.map((c) => {
            const isCurrentActive = c.company_id === currentCompanyId;
            const isLoading = switching === c.company_id;
            const bloqueada = c.is_usable === false;

            return (
              <button
                key={c.company_id}
                onClick={() => handleSelect(c.company_id)}
                disabled={!!switching || bloqueada}
                className={cn(
                  "w-full flex items-center gap-4 p-5 rounded-2xl border-2 transition-all duration-200",
                  "hover:shadow-medium hover:scale-[1.01] active:scale-[0.99]",
                  "disabled:opacity-60 disabled:cursor-not-allowed",
                  isCurrentActive
                    ? "border-primary bg-primary/5 shadow-soft"
                    : "border-border bg-card hover:border-primary/40"
                )}
              >
                <div className={cn(
                  "h-12 w-12 rounded-xl flex items-center justify-center shrink-0",
                  isCurrentActive ? "bg-primary/15" : "bg-muted"
                )}>
                  {c.company_logo ? (
                    <ImgOffline src={c.company_logo} alt="" className="h-8 w-8 object-contain rounded-lg" />
                  ) : (
                    <Building2 className={cn("h-6 w-6", isCurrentActive ? "text-primary" : "text-muted-foreground")} />
                  )}
                </div>

                <div className="flex-1 text-left min-w-0">
                  <p className="font-semibold text-foreground truncate">{c.company_name}</p>
                  {bloqueada ? (
                    <p className="text-xs text-muted-foreground font-medium">{t("perito.invitacion.sinSuscripcion")}</p>
                  ) : isCurrentActive && (
                    <p className="text-xs text-primary font-medium">{t("perito.multiCompany.currentActive")}</p>
                  )}
                </div>

                {isLoading ? (
                  <Loader2 className="h-5 w-5 animate-spin text-primary shrink-0" />
                ) : (
                  <ChevronRight className="h-5 w-5 text-muted-foreground shrink-0" />
                )}
              </button>
            );
          })}
        </div>
      </div>
    </div>
  );
}
